import { useState, useEffect, useRef } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { formatCurrency, formatDate } from '@/utils/formatUtils';
import closeIcon from '@/assets/close.svg';

const WithdrawDetailsModal = ({ isOpen, onClose, withdrawalData, disableBg = false }) => {
  const { t } = useTranslation();
  const [isAnimatingIn, setIsAnimatingIn] = useState(false);
  const panelRef = useRef(null);

  // Animation effects
  useEffect(() => {
    if (isOpen) {
      // Force a reflow to ensure the initial state is applied
      if (panelRef.current) {
        panelRef.current.offsetHeight;
      }
      // Use requestAnimationFrame to ensure the animation starts after the element is rendered
      requestAnimationFrame(() => {
        setIsAnimatingIn(true);
      });
    } else {
      setIsAnimatingIn(false);
    }
  }, [isOpen]);

  if (!isOpen || !withdrawalData) return null;

  return (
    <div className={`fixed inset-0 ${disableBg ? '' : 'bg-black/60'} flex flex-col justify-center items-end !border-t-0 z-50`}>
      <div 
        ref={panelRef}
        className={`w-[480px] h-full bg-white flex flex-col relative transition-transform duration-300 ease-out ${
          isAnimatingIn ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="w-full h-[72px] py-6 px-6 relative flex justify-start items-center gap-2.5">
          <div className="flex-1 py-6 flex justify-start items-center">
            <div className="flex justify-start items-center gap-2">
              <div className="text-[#111111] text-lg font-bold leading-[27px]">
                {t('withdraw.details.title')}
              </div>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="w-6 h-6 absolute right-6 top-6 flex items-center justify-center"
          >
            <img src={closeIcon} alt="close" className="w-7 h-7" />
          </button>
        </div>

        {/* Content */}
        <div className="self-stretch flex-1 py-6 px-6 flex flex-col justify-start items-end gap-6">
          {/* Amount Breakdown */}
          <div className="self-stretch h-[21px] relative">
            <div className="absolute right-0 top-0 text-right text-[#111111] text-sm font-medium leading-[21px]">
              {formatCurrency(withdrawalData.amount)}
            </div>
            <div className="absolute left-0 top-[0.50px] inline-flex justify-start items-center gap-1">
              <div className="text-[#111111] text-sm font-normal leading-[21px]">
                {t('withdraw.details.salesAmount')}
              </div>
            </div>
          </div>

          {/* Fee */}
          <div className="self-stretch h-[21px] relative">
            <div className="absolute right-0 top-0 text-right text-[#111111] text-sm font-medium leading-[21px]">
              {formatCurrency(withdrawalData.fee)}
            </div>
            <div className="absolute left-0 top-[0.50px] inline-flex justify-start items-center gap-1">
              <div className="text-[#111111] text-sm font-normal leading-[21px]">
                {t('withdraw.details.transferFee')}
              </div>
            </div>
          </div>

          {/* Final Payout Amount */}
          <div className="self-stretch h-[21px] relative">
            <div className="absolute right-0 top-[-3px] text-right text-[#ff5501] text-lg font-medium leading-[27px]">
              {formatCurrency(withdrawalData.payout)}
            </div>
            <div className="absolute left-0 top-[0.50px] inline-flex justify-start items-center gap-1">
              <div className="text-[#111111] text-sm font-normal leading-[21px]">
                {t('withdraw.details.transferAmount')}
              </div>
            </div>
          </div>

          {/* Details Section */}
          <div className="self-stretch pt-6 border-t border-[#f4f4f4] flex flex-col justify-start items-start gap-4">
            <div className="self-stretch flex flex-col justify-start items-start gap-2">
              {/* Application Date */}
              <div className="self-stretch h-[22px] min-w-80 inline-flex justify-start items-start gap-6">
                <div className="w-36 text-[#aaaaaa] text-sm font-normal leading-snug">
                  {t('withdraw.details.applicationDate')}
                </div>
                <div className="flex-1 text-right text-[#111111] text-sm font-normal leading-snug">
                  {withdrawalData.createdAt ? formatDate(withdrawalData.createdAt) : '-'}
                </div>
              </div>

              {/* Registered Business */}
              <div className="self-stretch min-w-80 inline-flex justify-start items-start gap-6">
                <div className="w-36 text-[#aaaaaa] text-sm font-normal leading-snug">
                  {t('withdraw.details.registeredBusiness')}
                </div>
                <div className="flex-1 text-right text-[#111111] text-sm font-normal leading-snug">
                  <Trans i18nKey="withdraw.details.companyInfo" components={{ br: <br />}} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WithdrawDetailsModal;
